<template>
  <Head title="Profil Jurusan" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="space-y-6">
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Profil Jurusan', href: route('profil-jurusan.index') },
          ]"
        />

        <!-- Table -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <div class="flex items-center justify-between">
              <h2 class="flex items-center gap-2 text-lg font-bold text-white">
                <i class="fa-solid fa-building-columns"></i>
                Daftar Profil Jurusan
              </h2>
              <span class="px-2.5 py-0.5 text-xs font-medium text-indigo-100 bg-white/20 rounded-full">
                {{ profil_jurusans.length }} data
              </span>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="py-3 pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-10">#</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Tentang</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hidden md:table-cell">Visi</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hidden lg:table-cell">Kelengkapan</th>
                  <th class="py-3 pl-3 pr-6 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Aksi</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr v-if="!profil_jurusans.length">
                  <td colspan="5" class="py-12 text-center text-sm text-gray-400">
                    <i class="fa-solid fa-inbox text-2xl mb-2 block text-gray-300"></i>
                    Belum ada data profil jurusan.
                  </td>
                </tr>
                <tr
                  v-for="(profil, index) in profil_jurusans"
                  :key="profil.id"
                  class="hover:bg-gray-50 transition-colors"
                  :class="{ 'bg-indigo-50/60': form.id === profil.id }"
                >
                  <td class="py-3.5 pl-6 pr-3 text-sm font-medium text-gray-500">{{ index + 1 }}</td>
                  <td class="px-4 py-3.5 text-sm text-gray-900 max-w-xs">
                    <p class="line-clamp-2 text-gray-700">{{ stripHtml(profil.tentang) || '—' }}</p>
                  </td>
                  <td class="hidden md:table-cell px-4 py-3.5 text-sm text-gray-700 max-w-xs">
                    <p class="line-clamp-2">{{ stripHtml(profil.visi) || '—' }}</p>
                  </td>
                  <td class="hidden lg:table-cell px-4 py-3.5">
                    <div class="flex flex-wrap gap-1.5">
                      <span
                        v-for="field in ['misi', 'akreditasi']"
                        :key="field"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                        :class="profil[field]
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-400 border border-gray-200'"
                      >
                        <i :class="profil[field] ? 'fa-solid fa-check' : 'fa-solid fa-minus'" class="text-[10px]"></i>
                        {{ field.charAt(0).toUpperCase() + field.slice(1) }}
                      </span>
                    </div>
                  </td>
                  <td class="py-3.5 pl-3 pr-6">
                    <div class="flex items-center justify-end gap-2">
                      <button
                        @click="editProfil(profil)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                        :class="{ 'ring-2 ring-indigo-400': form.id === profil.id }"
                        title="Edit"
                      >
                        <i class="fa-solid fa-pen-to-square"></i>
                        <span class="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        @click="destroy(profil.id)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        title="Hapus"
                      >
                        <i class="fa-solid fa-trash"></i>
                        <span class="hidden sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Form -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <h2 class="flex items-center gap-2 text-lg font-bold text-white">
              <i :class="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'"></i>
              {{ isEditMode ? 'Edit Profil Jurusan' : 'Tambah Profil Jurusan' }}
            </h2>
          </div>

          <form @submit.prevent="submitForm" class="p-6 space-y-6">
            <!-- Tentang + Visi -->
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <InputLabel value="Tentang" class="mb-1.5" />
                <div class="border border-gray-200 rounded-lg overflow-hidden">
                  <QuillEditor
                    v-model:content="form.tentang"
                    toolbar="essential"
                    contentType="html"
                    class="quill-field"
                    :ref="quillTentangRef"
                  />
                </div>
                <InputError :message="form.errors.tentang" class="mt-1.5" />
              </div>
              <div>
                <InputLabel value="Visi" class="mb-1.5" />
                <div class="border border-gray-200 rounded-lg overflow-hidden">
                  <QuillEditor
                    v-model:content="form.visi"
                    toolbar="essential"
                    contentType="html"
                    class="quill-field"
                    :ref="quillVisiRef"
                  />
                </div>
                <InputError :message="form.errors.visi" class="mt-1.5" />
              </div>
            </div>

            <!-- Misi + Akreditasi -->
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <InputLabel value="Misi" class="mb-1.5" />
                <div class="border border-gray-200 rounded-lg overflow-hidden">
                  <QuillEditor
                    v-model:content="form.misi"
                    toolbar="essential"
                    contentType="html"
                    class="quill-field"
                    :ref="quillMisiRef"
                  />
                </div>
                <InputError :message="form.errors.misi" class="mt-1.5" />
              </div>
              <div>
                <InputLabel value="Akreditasi" class="mb-1.5" />
                <div class="border border-gray-200 rounded-lg overflow-hidden">
                  <QuillEditor
                    v-model:content="form.akreditasi"
                    toolbar="essential"
                    contentType="html"
                    class="quill-field"
                    :ref="quillAkreditasiRef"
                  />
                </div>
                <InputError :message="form.errors.akreditasi" class="mt-1.5" />
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <button
                v-if="isEditMode"
                type="button"
                @click="cancelEdit"
                class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i class="fa-solid fa-times mr-1.5"></i>Batal
              </button>
              <PrimaryBtn
                :disabled="form.processing"
                class="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i class="fa-solid fa-floppy-disk"></i>
                {{ form.processing ? 'Menyimpan...' : isEditMode ? 'Simpan Perubahan' : 'Simpan' }}
              </PrimaryBtn>
            </div>
          </form>
        </div>

      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, router, useForm } from "@inertiajs/vue3";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import { QuillEditor } from "@vueup/vue-quill";
import { ref, inject } from "vue";
import Swal from "sweetalert2";

const route = inject('route');
const props = defineProps({ profil_jurusans: Array });

const isEditMode = ref(false);
const form = useForm({
  id: null,
  tentang: "",
  visi: "",
  misi: "",
  akreditasi: "",
});
const quillVisiRef = ref(null);
const quillMisiRef = ref(null);
const quillAkreditasiRef = ref(null);
const quillTentangRef = ref(null);

const stripHtml = (html) =>
  (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);

function submitForm() {
  const routeName = form.id ? "profil-jurusan.update" : "profil-jurusan.store";
  const method = form.id ? "put" : "post";
  const routeArg = form.id ? route(routeName, form.id) : route(routeName);

  Swal.fire({ title: "Menyimpan...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  form[method](routeArg, {
    onSuccess: () => {
      resetForm();
      Swal.fire({
        title: "Berhasil!",
        text: form.id ? "Profil jurusan berhasil diperbarui" : "Profil jurusan berhasil disimpan",
        icon: "success",
        confirmButtonText: "OK",
      });
    },
    onError: () => {
      Swal.fire({ title: "Error!", text: "Terjadi kesalahan saat menyimpan data", icon: "error" });
    },
  });
}

function resetForm() {
  isEditMode.value = false;
  form.reset();
  form.id = null;
  form.tentang = form.visi = form.misi = form.akreditasi = "";
  resetQuillEditors();
}

function resetQuillEditors() {
  for (const r of [quillTentangRef, quillVisiRef, quillMisiRef, quillAkreditasiRef]) {
    if (r.value) {
      r.value.setHTML?.("");
      r.value.editor?.setContents?.([]);
    }
  }
}

function editProfil(profil) {
  isEditMode.value = true;
  form.id       = profil.id;
  form.tentang  = profil.tentang   || "";
  form.visi     = profil.visi      || "";
  form.misi     = profil.misi      || "";
  form.akreditasi = profil.akreditasi || "";

  setTimeout(() => {
    quillTentangRef.value?.setHTML?.(form.tentang);
    quillVisiRef.value?.setHTML?.(form.visi);
    quillMisiRef.value?.setHTML?.(form.misi);
    quillAkreditasiRef.value?.setHTML?.(form.akreditasi);

    // Scroll ke form
    document.querySelector('.rounded-xl:last-child')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
}

function cancelEdit() {
  resetForm();
}

function destroy(id) {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Data yang dihapus tidak dapat dikembalikan!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (!result.isConfirmed) return;
    router.delete(route("profil-jurusan.destroy", id), {
      onSuccess: () => {
        if (form.id === id) resetForm();
        Swal.fire({ title: "Berhasil!", text: "Data berhasil dihapus.", icon: "success" });
      },
      onError: () => {
        Swal.fire({ title: "Error!", text: "Terjadi kesalahan saat menghapus data.", icon: "error" });
      },
    });
  });
}
</script>

<style scoped>
.quill-field {
  min-height: 220px;
  background: #fff;
}
.quill-field :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 6px 10px;
}
.quill-field :deep(.ql-container) {
  border: none;
  font-size: 0.9rem;
  min-height: 180px;
}
.quill-field :deep(.ql-editor) {
  min-height: 180px;
  padding: 12px 14px;
  line-height: 1.6;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
